'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Globe, Sun, Moon, Mail, ArrowRight } from 'lucide-react';
import { useAppStore } from '@/lib/themeStore';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

interface ForgotPasswordForm {
  email: string;
}

export default function ForgotPasswordClient() {
  const { locale, theme, toggleTheme, setLocale } = useAppStore();
  const router = useRouter();
  const isRTL = locale === 'ar';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sentTo, setSentTo] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>({
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`,
      });
      if (resetError) {
        setError(
          locale === 'ar' ?'حدث خطأ أثناء إرسال البريد. حاول مرة أخرى.' :'Failed to send reset email. Please try again.'
        );
        return;
      }
      setSentTo(data.email);
      setEmailSent(true);
    } catch {
      setError(
        locale === 'ar' ?'حدث خطأ غير متوقع. حاول مرة أخرى.' :'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ backgroundColor: 'var(--background)' }}
    >
      {/* Brand panel */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1A1D27 0%, #0F1117 60%, #1A1030 100%)',
          borderInlineEnd: '1px solid var(--border)',
        }}
      >
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, var(--primary) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, var(--accent) 0%, transparent 50%)`,
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-xl font-bold" style={{ color: 'var(--primary)' }}>DayFlow</span>
          </div>
          <div className="mb-8">
            <h1 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight" style={{ color: 'var(--foreground)' }}>
              {locale === 'ar' ? 'استعادة الوصول إلى حسابك' : 'Recover Access to Your Account'}
            </h1>
            <p className="text-base" style={{ color: 'var(--muted-foreground)', lineHeight: 1.7 }}>
              {locale === 'ar' ?'أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.' :'Enter your email and we\'ll send you a secure link to reset your password.'}
            </p>
          </div>
          <div className="space-y-3">
            {[
              locale === 'ar' ? 'رابط آمن ومشفر' : 'Secure encrypted link',
              locale === 'ar' ? 'صالح لمدة 60 دقيقة' : 'Valid for 60 minutes',
              locale === 'ar' ? 'لا حاجة لتذكر كلمة المرور القديمة' : 'No need to remember old password',
            ].map((item, idx) => (
              <div key={`feat-${idx}`} className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(108,99,255,0.2)' }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5L4 7L8 3" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            © 2026 DayFlow · {locale === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2 lg:hidden">
            <AppLogo size={28} />
            <span className="font-bold text-sm" style={{ color: 'var(--primary)' }}>DayFlow</span>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <button
              onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-muted"
              style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
            >
              <Globe size={14} />
              {locale === 'ar' ? 'EN' : 'AR'}
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-colors hover:bg-muted"
              style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            {/* Back link */}
            <Link
              href="/login"
              className="inline-flex items-center gap-2 text-sm mb-8 transition-colors hover:opacity-80"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <ArrowLeft size={15} className={isRTL ? 'rotate-180' : ''} />
              {locale === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
            </Link>

            {!emailSent ? (
              <>
                <div className="mb-8">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(108,99,255,0.15)' }}
                  >
                    <Mail size={22} style={{ color: 'var(--primary)' }} />
                  </div>
                  <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
                    {locale === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot your password?'}
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    {locale === 'ar' ?'أدخل بريدك الإلكتروني المسجل وسنرسل لك رابط إعادة التعيين.' :'Enter your registered email and we\'ll send you a reset link.'}
                  </p>
                </div>

                {error && (
                  <div
                    className="flex items-center gap-2 px-4 py-3 rounded-lg mb-4 text-sm"
                    style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.2)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      {locale === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                    </label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder={locale === 'ar' ? 'example@company.sa' : 'you@company.com'}
                      dir="ltr"
                      {...register('email', {
                        required: locale === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: locale === 'ar' ? 'بريد إلكتروني غير صالح' : 'Invalid email address',
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full"
                    style={{ height: '44px' }}
                  >
                    {isLoading ? (
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                      </svg>
                    ) : (
                      <>
                        {locale === 'ar' ? 'إرسال رابط الاستعادة' : 'Send Reset Link'}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              /* Success state */
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'var(--success-bg)', color: 'var(--success)' }}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
                  {locale === 'ar' ? 'تم إرسال الرابط!' : 'Reset Link Sent!'}
                </h3>
                <p className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  {locale === 'ar' ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى:' : 'We sent a password reset link to:'}
                </p>
                <p className="text-sm font-semibold mb-6 tabular-nums" dir="ltr" style={{ color: 'var(--foreground)' }}>
                  {sentTo}
                </p>
                <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
                  {locale === 'ar' ?'تحقق من صندوق الوارد (وربما مجلد البريد العشوائي). الرابط صالح لمدة 60 دقيقة.' :'Check your inbox (and spam folder). The link is valid for 60 minutes.'}
                </p>
                <Link href="/login" className="btn-primary w-full flex items-center justify-center gap-2" style={{ height: '44px' }}>
                  {locale === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
